import {
  useCallback,
  type MutableRefObject,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import type { SpinButtonHandler } from '../types.js';

/**
 * This hook provides ootb support for arrow key and backspace navigation between spinbuttons within the group.
 * @param spinButtonRefs - A sparse array of spinbutton handlers
 * @param triggerRef - A sparse array of trigger keys
 */
export const useNavigateSpinButton = (
  spinButtonRefs: MutableRefObject<SpinButtonHandler[]>,
  triggerRefs: MutableRefObject<string[]>,
) => {
  // the keydown handler to attach to the spinbutton
  const onKeyDownHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      // Condensed version of the handlers for simpler usage
      const handlers = spinButtonRefs.current.filter(Boolean);
      const index = handlers?.findIndex(
        ({ element }) => element?.current === e?.target,
      );

      // Don't do anything, if the event target is not in our spin button array
      if (index < 0) {
        return;
      }

      const [prev, target, next] = [
        handlers[index - 1],
        handlers[index],
        handlers[index + 1],
      ];

      const length = target?.element?.current?.textContent?.length;
      const selection = target?.getSelection();
      switch (e.key) {
        case 'ArrowLeft':
          // If a prev. spinbutton is available and the cursor is at the first position
          if (prev && selection && selection.start === 0) {
            prev.element.current?.focus();
            e.preventDefault();
          }
          return;
        case 'ArrowRight':
          // If a next spinbutton is available and the cursor is at the last position
          if (next && selection && selection.end === length) {
            next.element.current?.focus();
            e.preventDefault();
          }
          return;
        case 'Backspace':
          // If a prev. spinbutton is available and the cursor is collapsed at the first position
          if (
            prev &&
            selection &&
            selection.start === 0 &&
            selection.end === 0
          ) {
            prev.element.current?.focus();
            // Set the cursor to the last position of the prev. spinbutton
            prev.setSelection({ start: 0, end: 0, rtl: true });
            e.preventDefault();
          }
          return;
      }

      // Search for a defined trigger after the current target index
      const lookupIndex = spinButtonRefs.current?.findIndex(
        (h) => h === target,
      );
      const nextTriggerIndex = triggerRefs.current.findIndex(
        (trigger, index) => trigger !== undefined && index > lookupIndex,
      );

      // Find the first spinbutton after the trigger index
      const afterTriggerSpinButton = spinButtonRefs.current?.find(
        (spinbutton, index) => spinbutton && index > nextTriggerIndex,
      );
      const trigger = triggerRefs.current[nextTriggerIndex];

      // If a trigger and a after trigger spinbutton was found, focus the spinbutton if the event key matches the trigger key.
      if (
        trigger !== undefined &&
        afterTriggerSpinButton &&
        e.key === trigger
      ) {
        afterTriggerSpinButton.element.current?.focus();
        e.preventDefault();
      }
    },
    [spinButtonRefs, triggerRefs],
  );

  return { onKeyDownHandler };
};
